'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { OrganizationSwitcher } from '@/components/organization/OrganizationSwitcher';
import { Skeleton } from '@/components/ui';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

export function OnboardingNavigation() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <nav className="bg-background/80 border-border fixed top-0 z-50 w-full border-b px-6 py-3 backdrop-blur-md">
        <div
          className="mx-auto flex items-center justify-between"
          style={{ maxWidth: theme.spacing.maxWidth }}
        >
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-6 w-28" />
          </div>
        </div>
      </nav>
    );
  }

  const isDark = resolvedTheme === 'dark';

  return (
    <nav
      className="fixed top-0 z-50 w-full px-6 py-3"
      style={{
        backdropFilter: theme.glass.card.backdropBlur,
        backgroundColor:
          theme.backgrounds.navigation.transparent[isDark ? 'dark' : 'light'],
        borderBottom: `1px solid ${theme.glass.card.border[isDark ? 'dark' : 'light']}`,
      }}
    >
      <div
        className="mx-auto flex items-center justify-between"
        style={{ maxWidth: theme.spacing.maxWidth }}
      >
        {/* Left: Logo + Mindrealm text (non-clickable) */}
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg text-2xl"
            style={{
              background: '#5b21b6',
              fontFamily: theme.fonts.display,
              textShadow: '2px 3px 4px rgba(0, 0, 0, 0.5)',
            }}
          >
            🪲
          </div>
          <span
            className="text-gradient-hero text-2xl font-bold"
            style={{
              fontFamily: theme.fonts.display,
            }}
          >
            Mindrealm
          </span>
        </div>

        {/* Center: Onboarding link (non-clickable, shows active) */}
        <div className="hidden items-center gap-8 md:flex">
          <span
            className="cursor-default"
            style={{
              fontFamily: theme.fonts.body,
              fontSize: theme.typography.scale.body.fontSize,
              fontWeight: '700',
              color: theme.colors.text.primary[isDark ? 'dark' : 'light'],
              textDecoration: 'underline',
              textDecorationColor: theme.focusRings.primary.color,
              textUnderlineOffset: '4px',
            }}
          >
            Onboarding
          </span>
        </div>

        {/* Right: organization switcher escape hatch. Only renders for multi-organization users (self-gates
            to >1 organization), so a single-organization user sees nothing. Lets a user redirected here for
            an organization they didn't mean to set up switch to another organization and land on its
            dashboard. min-width keeps the centered "Onboarding" label balanced when empty. */}
        <div
          className="hidden items-center justify-end gap-3 md:flex"
          style={{ minWidth: '80px' }}
        >
          <OrganizationSwitcher />
        </div>

        {/* Mobile: Empty space for alignment */}
        <div className="md:hidden" style={{ width: '40px' }} />
      </div>
    </nav>
  );
}
